"use client";

import { CodeBlock, CodeBlockCode, CodeBlockGroup } from "../ui/code-block";
import { Button } from "../ui/button";
import { useState } from "react";
import { Input } from "../ui/input";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Mic,
  MicOff,
  Phone,
  Monitor,
  Calendar,
  MessageSquare,
  Columns3Icon,
  Cog,
  Globe,
} from "lucide-react";
import { useInBrowserCall } from "../../hooks/useInBrowserCall";
import { api } from "../../trpc/react";
import { ComicText } from "../ui/comic-text";
import { motion } from "motion/react";
import Image from "next/image";
import { Label } from "../ui/label";
import Balancer from "react-wrap-balancer";

export function CalSetterHero() {
  return (
    <section className="m-auto">
      <div className="h-full w-full m-auto">
        <div className="w-full h-full">
          {/* Hero content */}
          <div className="text-center h-full flex flex-col items-center gap-sm md:gap-xl">
            <motion.div
              className="flex flex-row gap-0 text-center items-center"
              variants={{
                hidden: { opacity: 0 },
                visible: {
                  opacity: 1,
                  transition: {
                    staggerChildren: 0.3,
                  },
                },
              }}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.8, rotate: -2 }}
                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                transition={{
                  duration: 0.6,
                  ease: [0.175, 0.885, 0.32, 1.275],
                  type: "spring",
                }}
                className="cursor-pointer hover:rotate-12 transition-transform duration-500 ease-in-out"
              >
                <Image
                  src={"/callcow_transparent.png"}
                  alt={"callcow"}
                  width={256}
                  height={256}
                />
              </motion.div>
              <h1 className="text-lg sm:text-xl md:text-3xl my-lg font-medium bg-clip-text text-text-landing">
                <ComicText>Call Anyone</ComicText>
                <ComicText>For Anything</ComicText>
              </h1>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20, rotate: -1 }}
              animate={{ opacity: 1, y: 0, rotate: 0 }}
              transition={{
                duration: 0.8,
                ease: [0.175, 0.885, 0.32, 1.275],
                delay: 0.3,
              }}
              className="relative group"
            >
              <div className="absolute -inset-2 bg-gradient-to-r from-gray-900 via-gray-700 to-gray-900 rounded-2xl blur-lg opacity-60 group-hover:opacity-80 transition-opacity animate-pulse"></div>
              <div className="relative bg-white/10 backdrop-blur-sm border-4 border-white/30 rounded-2xl overflow-hidden shadow-2xl transform hover:scale-105 transition-all duration-300">
                <div className="absolute top-2 left-2 flex gap-1 z-10">
                  <div className="w-3 h-3 bg-red-500 rounded-full border-2 border-white/50"></div>
                  <div className="w-3 h-3 bg-yellow-500 rounded-full border-2 border-white/50"></div>
                  <div className="w-3 h-3 bg-green-500 rounded-full border-2 border-white/50"></div>
                </div>
                <video
                  width={720}
                  controls
                  className="relative z-0 drop-shadow-2xl filter contrast-110 brightness-105 saturate-110"
                >
                  <source
                    src="https://7ai6242t6b.ufs.sh/f/u3o5DFaG3ABg6pE5OoHmhue0s8KqdZoLIV92aADyvbwcRn1f"
                    type="video/mp4"
                  />
                  Your browser does not support the video tag.
                </video>
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-gray-800 via-gray-600 to-gray-800"></div>
              </div>
            </motion.div>

            <div className="w-full flex flex-col items-center gap-xl my-xl gap-md">
              <ComicText fontSize={3}> ⬇️ Try it yourself ⬇️</ComicText>

              <CalSetterDemoForm />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const formSchema = z.object({
  name: z.string().min(1, "Please enter a name"),
  phoneNumber: z
    .string()
    .min(1, "Phone number is required")
    .regex(/^\+?[\d\s\-\(\)]{10,}$/, "Please enter a valid phone number"),
  email: z.string().email("Please enter a valid email address"),
  useCase: z.enum(["book-meeting", "information-collection"], {
    required_error: "Please select a use case",
  }),
});

type FormData = z.infer<typeof formSchema>;

export function CalSetterDemoForm() {
  const [callingStarted, setCallingStarted] = useState(false);
  const [agentType, setAgentType] = useState<
    "book-meeting" | "collect-info" | "other"
  >("book-meeting");

  const {
    isActive: inBrowserMode,
    isConnected,
    isMuted,
    isConnecting,
    startCall,
    endCall,
    toggleMute,
  } = useInBrowserCall();

  const newDemoCallMutation = api.calling.newDemoCall.useMutation();
  const recordForm = api.calling.recordForm.useMutation();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    mode: "onChange",
    defaultValues: {
      useCase: "book-meeting",
    },
  });

  async function startInBrowserCall(data: FormData) {
    if (callingStarted || inBrowserMode) return;

    await recordForm.mutateAsync({
      phoneNumber: data.phoneNumber,
      email: data.email,
      name: data.name,
    });

    startCall();
  }

  async function startPhoneCall(data: FormData) {
    if (callingStarted) return;
    setCallingStarted(true);

    try {
      await newDemoCallMutation.mutateAsync({
        phoneNumber: data.phoneNumber,
        email: data.email,
        name: data.name,
      });
    } catch (error) {
      console.error("Failed to start phone call:", error);
      setCallingStarted(false);
    }
  }

  function onSubmit(data: FormData, type?: "browser" | "phone") {
    const currentCallType = type;
    if (currentCallType === "browser") {
      startInBrowserCall(data);
    } else {
      startPhoneCall(data);
    }
  }

  const getUseCaseDescription = () => {
    switch (agentType) {
      case "book-meeting":
        return "Our AI agent will help you schedule a meeting and find the perfect time";
      case "collect-info":
        return "Our AI agent will collect information and ask follow-up questions";
      case "other":
        return "Let us build custom workflows for you";
      default:
        return "Our AI agent will assist you";
    }
  };

  return (
    <div className="w-full max-w-[900px] flex gap-6">
      {/* Custom Tab Headers - Left Side */}
      <div className="flex flex-col gap-2 w-48 mt-4">
        {[
          {
            type: "book-meeting",
            icon: <Calendar className="w-5 h-5" />,
            label: "BOOK MEETING",
          },
          {
            type: "collect-info",
            icon: <MessageSquare className="w-5 h-5" />,
            label: "COLLECT INFO",
          },
          {
            type: "other",
            icon: <Cog className="w-5 h-5" />,
            label: "CUSTOM",
          },
        ].map((item) => (
          <div
            key={item.type}
            onClick={() =>
              setAgentType(item.type as "book-meeting" | "collect-info")
            }
            className={`flex items-center justify-center gap-2 font-black text-lg transform hover:scale-105 transition-all duration-200 tracking-wider cursor-pointer rounded-md h-18 ${
              agentType === item.type
                ? "bg-white text-black shadow-lg scale-105"
                : "text-gray-300 hover:text-white bg-gray-500 opacity-80"
            }`}
            style={{
              textShadow:
                agentType === item.type
                  ? "none"
                  : "2px 2px 4px rgba(0,0,0,0.8)",
              filter:
                agentType === item.type
                  ? "drop-shadow(0 4px 8px rgba(0,0,0,0.3))"
                  : "none",
            }}
          >
            {item.icon}
            <span className="text-base">{item.label}</span>
          </div>
        ))}
      </div>

      {/* Form - Right Side */}
      <div className="flex-1 flex w-full flex-col mt-4 overflow border border-border bg-card text-card-foreground rounded-xl">
        <form>
          <div className="flex flex-col gap-4 p-4">
            <div className="flex flex-row gap-2 justify-center items-center">
              <Image
                src={"/cow_collect_info.png"}
                alt={"callcow"}
                width={100}
                height={100}
              />
              <span
                className="text-black"
                style={{
                  fontSize: `1.5rem`,
                  transform: "skewX(-10deg)",
                  textTransform: "uppercase",
                  backgroundColor: "#FFFFFF",
                  color: "black",
                  backgroundSize: "8px 8px",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                }}
              >
                <Balancer>{getUseCaseDescription()}</Balancer>
              </span>
            </div>
            {/* Contact Information */}
            <div className="space-y-3 font-inter">
              <div>
                <Input type="text" placeholder="Name" {...register("name")} />
                {errors.name && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.name.message}
                  </p>
                )}
              </div>
              <div>
                <Input
                  type="tel"
                  autoComplete="tel"
                  placeholder="Phone Number"
                  {...register("phoneNumber")}
                />
                {errors.phoneNumber && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.phoneNumber.message}
                  </p>
                )}
              </div>
              <div>
                <Input
                  type="email"
                  placeholder="Email"
                  {...register("email")}
                />
                {errors.email && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.email.message}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* In-Browser Call Controls */}
          {inBrowserMode && (
            <div className="px-4 py-3 border-t border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div
                    className={`w-3 h-3 rounded-full ${
                      isConnected
                        ? "bg-green-400"
                        : isConnecting
                          ? "bg-yellow-400"
                          : "bg-red-400"
                    }`}
                  />
                  <span className="text-sm text-gray-500">
                    {isConnected
                      ? "Connected"
                      : isConnecting
                        ? "Connecting..."
                        : "Disconnected"}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  {isConnected && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={toggleMute}
                    >
                      {isMuted ? (
                        <MicOff className="w-4 h-4" />
                      ) : (
                        <Mic className="w-4 h-4" />
                      )}
                    </Button>
                  )}

                  <Button
                    type="button"
                    variant="outlineDestructive"
                    size="sm"
                    onClick={() => {
                      endCall();
                      setCallingStarted(false);
                    }}
                  >
                    End Call
                  </Button>
                </div>
              </div>

              {isConnected && (
                <Label className="text-xs text-gray-800 mt-2">
                  🎙️ Speak now! The AI agent is listening and will respond.
                </Label>
              )}
            </div>
          )}

          <div className="px-4 py-2 flex items-center justify-between gap-2">
            <Button
              type="button"
              disabled={
                !isValid ||
                callingStarted ||
                newDemoCallMutation.isPending ||
                inBrowserMode ||
                isConnecting
              }
              className="w-full"
              onClick={handleSubmit((data) => onSubmit(data, "browser"))}
            >
              <Globe className="w-4 h-4 mr-2" />
              {isConnecting
                ? "INITIATING CALL..."
                : inBrowserMode
                  ? "CALL STARTED"
                  : "START BROWSER CALL"}
            </Button>
            <Button
              type="button"
              disabled={
                !isValid ||
                callingStarted ||
                newDemoCallMutation.isPending ||
                inBrowserMode ||
                isConnecting
              }
              className="w-full"
              onClick={handleSubmit((data) => onSubmit(data, "phone"))}
            >
              <Phone className="w-4 h-4 mr-2" />
              {newDemoCallMutation.isPending
                ? "INITIATING CALL..."
                : callingStarted
                  ? "OUR AI AGENT WILL CALL YOU SOON..."
                  : "START PHONE CALL"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
